import { Typography } from '@plato/ui/primitives/typography';
import { cn } from '@plato/ui/util/classes';
import { XIcon } from 'lucide-react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { useAskPlato } from './ask-plato-provider.tsx';

export type AskPlatoTeaserProps = {
    /**
     * Which edge the speech-bubble tail sticks out of, pointing at the
     * trigger: `down` for the teaser above the desktop FAB, `up` for the one
     * hanging below the mobile top-bar trigger.
     */
    tail: 'down' | 'up';
    /** Pressing the bubble itself opens the chat (and retires the teaser). */
    onOpen: () => void;
    /** The X retires the teaser — and the unread dot with it — for good. */
    onDismiss: () => void;
    className?: string;
};

/**
 * The dismissible speech bubble attached to the Ask Plato trigger that
 * invites first use. Teaser and unread dot are one state: both show until the
 * user opens the chat or dismisses the teaser, then both are gone for good.
 */
export function AskPlatoTeaser({ tail, onOpen, onDismiss, className }: AskPlatoTeaserProps): ReactElement {
    const { t } = useTranslation('ask-plato');

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onOpen}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onOpen();
                }
            }}
            className={cn(
                'relative max-w-64 cursor-pointer rounded-relaxed border border-line-weak bg-container',
                'py-cozy pr-8 pl-default shadow-lg transition-colors hover:bg-container-hover',
                className,
            )}
        >
            {/* Tail — a rotated square peeking out of the edge facing the trigger,
                centred on it: the bubble is right-aligned with the trigger, so the
                8px tail sits at (trigger size − 8px) / 2 from the right edge. */}
            <span
                aria-hidden
                className={cn(
                    'absolute size-2 rotate-45 bg-container',
                    tail === 'up'
                        ? // mobile top-bar avatar, 32px
                          '-top-1 right-3 border-t border-l border-line-weak'
                        : // desktop FAB, 52px
                          'right-5.5 -bottom-1 border-r border-b border-line-weak',
                )}
            />
            <Typography variant="body">{t('teaser')}</Typography>
            <button
                type="button"
                aria-label={t('teaser_dismiss')}
                onClick={(event) => {
                    event.stopPropagation();
                    onDismiss();
                }}
                className={cn(
                    'absolute top-1.5 right-1.5 cursor-pointer rounded-default p-1 text-content-weak',
                    'transition-colors after:absolute after:-inset-1.5',
                    'hover:bg-content/(--opacity-wash) hover:text-content',
                )}
            >
                <XIcon className="size-3.5" />
            </button>
        </div>
    );
}

export type AskPlatoConnectedTeaserProps = Pick<AskPlatoTeaserProps, 'tail' | 'className'>;

/**
 * The teaser wired to the provider: renders only while the teaser state is
 * live, opens the chat on press (reported as its own entry point), and
 * retires the state on dismiss.
 */
export function AskPlatoConnectedTeaser({
    tail,
    className,
}: AskPlatoConnectedTeaserProps): ReactElement | undefined {
    const { showTeaser, openAskPlato, dismissTeaser } = useAskPlato();
    const sendEvent = useAnalytics();

    if (!showTeaser) {
        return undefined;
    }

    return (
        <AskPlatoTeaser
            tail={tail}
            className={className}
            onOpen={() => {
                openAskPlato('teaser');
            }}
            onDismiss={() => {
                sendEvent('ai_chat_teaser_dismissed', { placement: tail === 'down' ? 'fab' : 'top_bar' });
                dismissTeaser();
            }}
        />
    );
}
